import { useNavigate, useRouter } from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'

/**
 * Out of the open documents, to where the reader was before the first of them.
 *
 * Moving between open documents is navigation, so one step back from a
 * document is the document before it. The way back on a document leaves the
 * documents instead: it returns to the last place that was not one, by
 * stepping back through history to that entry, so the list it was opened from
 * keeps its search and scroll. Arriving cold on a document, from a link or a
 * reload, there is no such place, and the declared path answers.
 */

/** An entry in the session history; `held` while that slot still holds it. */
type Entry = { href: string; index: number; held: boolean }

/** How many document screens are mounted; above zero, the reader is in one. */
let inDocument = 0
/** The last entry the reader left while not in a document. */
let outside: Entry | null = null

type History = ReturnType<typeof useRouter>['history']

const entryOf = (history: History): Entry => ({
  href: history.location.href,
  index: history.location.state.__TSR_index,
  held: true,
})

/**
 * Follow the history from the shell, which is mounted before any document is
 * opened. A move fires before the next screen mounts, so the count still says
 * whether the screen being left was a document. A replace leaves nothing: the
 * reader is still on the same entry, which a route redirecting to its document
 * rewrites before the document mounts. A push at or below the place recorded
 * writes over its slot, so stepping back by index would land somewhere else.
 */
export function useWatchDocumentExits(): void {
  const router = useRouter()
  useEffect(() => {
    let current = entryOf(router.history)
    return router.history.subscribe(({ action }) => {
      const next = entryOf(router.history)
      if (action.type !== 'REPLACE' && inDocument === 0) outside = current
      if (action.type === 'PUSH' && outside && next.index <= outside.index)
        outside = { ...outside, held: false }
      current = next
    })
  }, [router])
}

export function useLeaveDocument(fallback: string): () => void {
  const router = useRouter()
  const navigate = useNavigate()

  useEffect(() => {
    inDocument += 1
    return () => {
      inDocument -= 1
    }
  }, [])

  return useCallback(() => {
    const here = router.history.location.state.__TSR_index
    if (outside?.held && outside.index < here)
      router.history.go(outside.index - here)
    else if (outside) router.history.push(outside.href)
    else navigate({ to: fallback })
  }, [router, navigate, fallback])
}
